'use strict';

import 'core-js/features/array/at';

import 'Components/form/VZInputField';
import { appendParamToURL, getParamFromURL } from 'Util/urlUtils';
import { toggleButtonState, trackPdpEccomerceEvent } from 'Root/util/util';
import VZSpinner from 'Components/VZSpinner';
import PCHelpers from './postcodeCheckHelpers';
import AddressCheck from './AddressCheck';
import { setCookie, getCookie } from 'Util/cookies';
import { isIOS, scrollTo } from 'Util/util';

const spinner = document.querySelector('vz-spinner') || new VZSpinner();

/**
 * @class PostCodeCheck
 * @description the availability check component used in the BIF configurator and for a Page Designer component.
 * It performs an address check and an availability check for the given address.
 */
export default class VZPostCodeCheck extends HTMLFormElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.form = document.getElementById('product-form');
        this.sidebar = document.querySelector('.js-configurator-summary');
        this.pdpSubmitButtons = [...document.querySelectorAll('.js-pdp-submit-button button')];
        this.editMode = document.querySelector('.js-edit-mode');
        this.hiddenInputWithPAID = this.querySelector('[name="paid"]');
        this.postcodeInputElement = this.querySelector('.js-postcode-check-postcode');
        this.houseNumberInputElement = this.querySelector('.js-postcode-check-house-number');
        this.additionSelectElement = this.querySelector('.js-postcode-check-addition');
        this.submitButton = this.querySelector('button[type="submit"]');
        this.isPageDesignerComponent = !!document.querySelector('.js-pd-postcode-check');
        this.parentSelector = this.isPageDesignerComponent ? document.querySelector('.js-pd-postcode-check') : this.form;
        this.configuratorType = this.dataset.configuratorType;
        this.isOneNet = this.dataset.configuratorType === 'one-net';
        this.isBusinessInternet = this.dataset.configuratorType === 'internet';
        this.isSharedOneNetConfiguration = window.location.href.includes('sharedonenet');

        this.removeLocation = this.removeLocation.bind(this);
        this.updateChatInfo = this.updateChatInfo.bind(this);

        // Initialise the address check.
        this.addressCheck = new AddressCheck(this, this.additionSelectElement);

        this.addEventListener('submit', this.handleAvailabilityFormSubmit.bind(this));
        this.toggleButtonState();

        if (this.editMode || window.location.href.includes('paid=')) {
            this.autoSubmitForm();
        }

        [...this.querySelectorAll('input')].forEach((field) => {
            if (field.type === 'number') {
                field.addEventListener('input', PCHelpers.respectMaxLength);
                field.addEventListener('input', PCHelpers.stripLeadingZero);
                field.addEventListener('keydown', PCHelpers.preventDashDotComma);
            }

            // The order of eventListeners is important, toggle the button state after the above events are done
            field.addEventListener('input focus', this.toggleButtonState.bind(this));

            if (this.editMode || window.location.href.includes('paid=')) {
                field.addEventListener('input', PCHelpers.removePAID.bind(this));
            }
        });

        // Add an event listener to select fields change event
        [...this.querySelectorAll('select')].forEach((select) => {
            select.addEventListener('change', this.toggleButtonState.bind(this));
        });

        // Add an event listener to enable product configuration after one net seat is selected.
        document.addEventListener('oneNet:seatSelection', this.enableProductConfiguration.bind(this));
    }

    /**
     * @function toggleButtonState
     * @description Validates the form and toggles the location submit button state.
     */
    toggleButtonState() {
        if (this.checkValidity() || (this.postcodeInputElement.checkValidity() && this.houseNumberInputElement.checkValidity())) {
            this.additionSelectElement.removeAttribute('read-only');
            toggleButtonState(this.submitButton, true);
        } else {
            this.additionSelectElement.setAttribute('read-only', 'true');
            toggleButtonState(this.submitButton, false);
        }
    }

    /**
     * @function enableProductConfiguration
     * @description Remove transparent layer from the form and add pointer events.
     * This does/should not execute for the PD postcode check component.
     * @param {Event} event - The oneNet:seatSelection event fired from VZQuantitySelector fn. onChange().
     */
    enableProductConfiguration(event = null) {
        const oneNetTierCard = [...document.querySelectorAll('.js-one-net-variation-card')].map((card) => card.closest('.pdp-card-section'));
        const isOneNetSeatSelectionEventFired = event && event.type === 'oneNet:seatSelection';
        this.isOneNetSeatSelected = isOneNetSeatSelectionEventFired && event?.detail?.isOneNetSeatSelected;
        this.areOneNetSeatsBelowThreshold = isOneNetSeatSelectionEventFired && event?.detail?.areOneNetSeatsBelowThreshold;
        this.shouldDisableOneNetConfiguration = !this.isOneNetSeatSelected || this.areOneNetSeatsBelowThreshold; // Minimum requirement to enable the whole configurator.

        // Enable the sidebar on availability check success or if one net and a seat has been selected.
        const elements = [
            this.form,
            ...this.pdpSubmitButtons,
            ...(this.isOneNet ? oneNetTierCard : []),
            ...(this.isBusinessInternet || this.isOneNetSeatSelected || this.editMode || this.isSharedOneNetConfiguration ? [this.sidebar] : [])
        ];
        const elementsToEnable = elements.filter(Boolean); // Filter out falsy values.
        const oneNetCriteria = this.isOneNet && this.success && !this.isPageDesignerComponent;
        const businessInternetCriteria = this.isBusinessInternet && this.success && !this.unableToOrder && !this.isPageDesignerComponent;

        if (businessInternetCriteria || oneNetCriteria) {
            elementsToEnable.forEach((element) => {
                if (element) {
                    element.classList.remove('opacity-50', 'pe-none');
                    element.removeAttribute('disabled');
                }
            });
        }

        // For one net, on availability check success, we only enable the tier card, rest must be disabled and enabled only after selecting a seat, see fn disableProductConfiguration. Do not disable for edit mode, since seats are already selected
        if (oneNetCriteria && this.shouldDisableOneNetConfiguration && !this.editMode && !this.isSharedOneNetConfiguration)
            this.disableProductConfiguration.bind(this)({ isEditLocation: false });
    }

    /**
     * @function disableProductConfiguration
     * @description Fade out the form and remove pointer events. This does/should not execute for the PD postcode check component.
     * @param {boolean} isEditLocation - Default is true for when this function is being called by an event listener.
     */
    disableProductConfiguration({ isEditLocation = true }) {
        if (this.isPageDesignerComponent) return;
        const nonTierOneNetCards = [...document.querySelectorAll('.js-pdp-card-section:not(.js-one-net-variation-card)')];

        // Remove all olds cards
        if (this.isOneNet) {
            nonTierOneNetCards.forEach((card) => card.parentElement?.removeChild(card));
        }

        // If we are in edit mode, then we disable the entire form regardless of configurator type, the configurator specific elements will be re-evaluated in fn enableProductConfiguration.
        const elements = [...this.pdpSubmitButtons, ...(this.isOneNet && !isEditLocation ? nonTierOneNetCards : [this.form, this.sidebar])];

        // If one-net, disable sidebar if seats are below threshold after selection.
        if (this.shouldDisableOneNetConfiguration) elements.push(this.sidebar);

        elements.forEach((element) => {
            if (element) {
                element.classList.add('opacity-50', 'pe-none');
                element.setAttribute('disabled', '');
            }
        });

        // Reset contract duration
        const defaultDuration = document.querySelector('.js-contract-duration-card input[name="contractDuration"][value="36"]:not(:checked)');
        defaultDuration?.dispatchEvent(new CustomEvent('change'));
    }

    /**
     * @function removeLocation
     * @description Removes the previous location result.
     */
    removeLocation() {
        [this.parentElement.querySelector('.js-postcode-response'), document.querySelector('.js-current-address')].forEach((element) =>
            element?.parentElement.removeChild(element)
        );
    }

    /**
     * @function showEditLocation
     * @description Shows a button which allows a customer to edit the configuration.
     */
    showEditLocation() {
        const editButton = this.parentElement.querySelector('.js-postcode-edit');
        if (editButton) {
            editButton.addEventListener('click', this.removeLocation);
            editButton.addEventListener('click', PCHelpers.showForm.bind(this));
            editButton.addEventListener('click', this.disableProductConfiguration.bind(this));
        }
        spinner.stop();
    }

    /**
     * @function setAddress
     * @description Sets the address in the sidebar.
     * @returns {void} Early exit if this is the PD component version of VZPostcodeCheck.
     */
    setAddress() {
        if (this.isPageDesignerComponent) return;

        const extension = this.address.extension || this.address.houseNumberExtension || '';
        const template = `<div class="mb-2 info-card js-current-address">
            <div>${this.address.street} ${this.address.houseNumber} ${extension},</div>
            <div>${this.address.postalCode} ${this.address.city}</div>
            <input type="hidden" id="js-active-address-postalCode" name="active-address-postalCode" value="${this.address.postalCode}"/>
            <input type="hidden" id="js-active-address-houseNumber" name="active-address-houseNumber" value="${this.address.houseNumber}"/>
            <input type="hidden" id="js-active-address-houseNumberAddition" name="active-address-houseNumberAddition" value="${this.address.extension || ''}"/>
        </div>`;
        document.querySelector('#configuration-details .js-configurator-summary-group-list').insertAdjacentHTML('afterbegin', template);

        this.updateChatInfo();
    }

    /**
     * @function updateChatInfo
     * @description Will update the hidden input field (if available) containing information for the chat agent
     */
    updateChatInfo() {
        const dataField = document.getElementById('activeConfiguratorGroup');
        if (!dataField) return;

        dataField.dispatchEvent(
            new CustomEvent('locationChange', {
                detail: this.address || this.addressCheckAddressParts
            })
        );
    }

    /**
     * @function updateResult
     * @param {JSON} availabilityCheckResponseData - Response object containing the HTML response string.
     * @description Removes the previous location and inserts the new one.
     */
    updateResult(availabilityCheckResponseData) {
        const {
            success: isResponseSuccessful,
            serverErrors,
            paid,
            speed,
            html: responseHtml,
            address,
            unableToOrder = null,
            locationAlreadyInBasket = null,
            locationHasExistingSubscription = null
        } = availabilityCheckResponseData;
        this.success = isResponseSuccessful;
        this.unableToOrder = unableToOrder;
        this.removeLocation();

        // All the error scenarios, on which we push an event to the data layer. NOTE: not sent when locationAlreadyInBasket.
        if ((address?.active || !isResponseSuccessful || unableToOrder) && this.addressCheckAddressParts && !this.isOneNet) {
            PCHelpers.pushErrorsToDataLayer(this.addressCheckAddressParts, locationAlreadyInBasket);
        }

        // BO-1743: If the location is outside the serviceable area, we show a modal for BI.
        if (this.isBusinessInternet && unableToOrder && !locationHasExistingSubscription) {
            const locationUnavailableModal = document.getElementById('location-unavailable-modal');
            locationUnavailableModal.render();
        }

        if ((!isResponseSuccessful || locationAlreadyInBasket) && serverErrors.length) {
            PCHelpers.showFeedbackMessage.bind(this)(serverErrors[0]);
            return;
        }

        this.PAID = paid;
        this.availableSpeed = speed;
        this.insertAdjacentHTML('afterEnd', responseHtml); // Inject the success or error html.

        // Dispatch the availability:update event so the page designer components reassign the event listeners.
        document.dispatchEvent(new CustomEvent('availability:update'));

        // Prevent default scrolling on IOS, so document is scrolled to top once results are populated.
        if (isIOS()) {
            const outputWrapper = document.querySelector('.js-postcode-response');
            scrollTo(outputWrapper.getBoundingClientRect());
        }

        if (unableToOrder && this.addressCheckAddress) {
            PCHelpers.handleUnableToOrder.bind(this)();
        }
        if (!unableToOrder) {
            const responseHeadingElement = this.parentElement.querySelector('.js-availability-response-address');
            responseHeadingElement.removeAttribute('unabletoorder');
        }

        if ((typeof address !== 'string' && address) || this.isOneNet) {
            // In scenarios where unableToOrder is true, the address is a string of undefined, in that case, we use addressCheckAddressParts.
            this.address = typeof address === 'string' && (address.includes('undefined') || address.length === 0) ? this.addressCheckAddressParts : address;
            this.setAddress.call(this);
        }

        // Store the saved address in a cookie so it can be restored when the form has been cleared - BO-1660.
        const addressData = {
            postcode: this.postcodeInputElement.value,
            houseNumber: this.houseNumberInputElement.value,
            paid: paid
        };

        if (this.additionSelectElement.selectedIndex) {
            addressData.addition = this.additionSelectElement.getAttribute('value');
        }

        // Availability form address cookie "ADDRESS-{postcode}{housenumber}{addition}"
        const availabilityFormAddressCookieName = `ADDRESS-${addressData.postcode.replace(' ', '')}${addressData.houseNumber}${addressData.addition || ''}`;
        const availabilityFormAddressCookie = `availabilityFormAddressData-${availabilityFormAddressCookieName}`;

        setCookie(availabilityFormAddressCookie, JSON.stringify(addressData), window.location.hostname, 0.5);

        document.dispatchEvent(new CustomEvent('postcodeChanged'));

        this.setBiState(responseHtml);
        PCHelpers.hideForm.bind(this)();
    }

    /**
     * @function executeAvailabilityCheck
     * @description Performs the availability check when the form is submitted and the address check is successful.
     * Updates the DOM with location data.
     */
    executeAvailabilityCheck() {
        spinner.start();

        PCHelpers.fetchAddressAndAvailability(this.action, this)
            .then(this.updateResult.bind(this))
            .then(this.enableProductConfiguration.bind(this))
            .then(this.updateURLs.bind(this))
            .then(this.disableUnavailableSpeeds.bind(this))
            .catch(PCHelpers.handleErrors.bind(this))
            .finally(this.showEditLocation.bind(this));
    }

    /**
     * @function handleAvailabilityFormSubmit
     * @param {FormEvent} event - Form submit event.
     * @description Handles the address check when the availability form is submitted. Then, if successful, it kicks of the availability check.
     */
    handleAvailabilityFormSubmit(event) {
        event.preventDefault();
        if (!this.checkValidity()) return;

        trackPdpEccomerceEvent(this.configuratorType, {
            name: 'Postcode check',
            number: 2
        });

        // BO-1570 - Address check has to be performed before availability check so that the availability check does not fire if the address is not found.
        this.addressCheck
            .executeAddressCheck()
            .then(this.executeAvailabilityCheck.bind(this))
            .catch((error) => PCHelpers.showFeedbackMessage.bind(this)(error)) // Handle rejection from promise returned in fn executeAddressCheck.
            .finally(spinner.stop);
    }

    /**
     * @function updateURLs
     * @description Updates all URLs to have the PAID. This does/should not execute for the PD postcode check component.
     */
    updateURLs() {
        if (!(this.success && this.PAID)) return;

        [...this.parentSelector.querySelectorAll('[data-url]')].forEach((element) => {
            const url = element.getAttribute('data-url');
            const newURL = appendParamToURL(url, 'paid', this.PAID);
            element.setAttribute('data-url', newURL);
        });
    }

    /**
     * @function disableUnavailableSpeeds
     * @description Disables speeds which are not available in a given region.
     */
    disableUnavailableSpeeds() {
        if (!(this.success && this.availableSpeed)) return;

        const speedNumber = parseInt(this.availableSpeed, 10);
        [...this.parentSelector.querySelectorAll('[name="tier"]')].forEach((element) => {
            const elementValueNumber = parseInt(element.value, 10);
            if (elementValueNumber > speedNumber || (speedNumber === 1000 && elementValueNumber === 700)) {
                element.setAttribute('disabled', 'disabled');
                element.nextElementSibling.classList.add('opacity-50', 'pe-none');

                if (element.hasAttribute('checked')) {
                    element.removeAttribute('checked');
                    this.selectHighestPossibleTier();
                }
            } else {
                element.removeAttribute('disabled');
                element.nextElementSibling.classList.remove('opacity-50', 'pe-none');
            }
        });
    }

    /**
     * @function selectHighestPossibleTier
     * @description Sorts all the tiers on speed, filters out unavailable tiers and then makes the highest available tier active.
     * This does/should not execute for the PD postcode check component.
     */
    selectHighestPossibleTier() {
        if (this.isPageDesignerComponent) return;

        // Available tiers
        const tiers = [...this.form.querySelectorAll('[name="tier"]:not(:disabled)')];

        // Highest tier first
        const sortedTiers = tiers.sort((a, b) => parseInt(b.value, 10) - parseInt(a.value, 10));

        // First available tier
        const highestPossibleSpeed = sortedTiers.find(
            (tier) => parseInt(tier.value, 10) === parseInt(this.availableSpeed, 10) || parseInt(tier.value, 10) <= parseInt(this.availableSpeed, 10)
        );

        // A change event needs to be triggered to initiate a new variation fetch
        const event = new Event('change', {
            bubbles: true,
            cancelable: true
        });
        highestPossibleSpeed.dispatchEvent(event);
    }

    /**
     * @function autoSubmitForm
     * @description Will auto submit the form on page load if the user is in edit mode and PAID is available.
     */
    autoSubmitForm() {
        this.PAID = this.hiddenInputWithPAID && this.hiddenInputWithPAID.value !== 'null' ? this.hiddenInputWithPAID.value : getParamFromURL(window.location.href, 'paid');

        // Availability form address cookie "ADDRESS-{postcode}{housenumber}{addition}"
        const availabilityFormAddressCookieName = this.PAID;
        const { postcode, houseNumber, addition, paid } = JSON.parse(getCookie(`availabilityFormAddressData-${availabilityFormAddressCookieName}`));

        // Restore the form field values so that this.checkValidity() passes, and the fields are filled if the user chooses to edit the address - BO-1660.
        if (postcode && houseNumber) {
            this.postcodeInputElement.value = postcode;
            this.houseNumberInputElement.value = houseNumber;

            if (addition) {
                this.additionSelectElement.value = addition;
                this.additionSelectElement.setAttribute('value', addition);
            }

            // Call setFieldStatus so the fields are formatted correctly.
            [this.postcodeInputElement, this.houseNumberInputElement].forEach((input) => input.setFieldStatus());
        }

        if (this.PAID && this.checkValidity()) {
            this.hiddenInputWithPAID.value = paid;
            const customEvent = new CustomEvent('submit');
            this.dispatchEvent(customEvent);
        }
    }

    setBiState(responseHtml) {
        const template = document.createElement('template');
        template.innerHTML = responseHtml;
        const isOrderable = template.content.querySelector('[data-bi-orderable]')?.getAttribute('data-bi-orderable');
        this.parentElement.closest('[data-configurator-type]')?.setAttribute('data-bi-orderable', isOrderable);
    }
}

if (!window.customElements.get('vz-postcode-check')) {
    window.customElements.define('vz-postcode-check', VZPostCodeCheck, { extends: 'form' });
}
